<template>
	<div class="newsletter-edit-navbar">
		<div class="navbar-left">
			<!-- Placeholder for symmetry -->
		</div>
		<div class="navbar-center">
			<AppSwitch
				fixed-color
				left-icon="iconMobile"
				right-icon="iconDesktop"
				:checked="newsletterStore.previewMode === 'desktop'"
				@switched="togglePreviewMode"
			/>
		</div>
		<div class="navbar-right">
			<AppButtonIcon
				class="ml-8"
				icon-name="icon-points"
				color="var(--color-bg-primary-2)"
				prevent-change
				@click-icon="handleMoreOptions"
			/>
			<AppButtonIcon
				v-if="showSendButtons"
				class="ml-8"
				icon-name="icon-share"
				color="var(--color-bg-primary-2)"
				prevent-change
				@click-icon="handleSendTest"
			/>
			<AppButton
				v-if="showSendButtons"
				class="ml-8"
				:text="getString('send')"
				@click="handleSend" />
		</div>

		<!-- Send Bottom Sheet -->
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="sendSheetOpen" @toggling-popup="toggleSendSheet">
			<NewsletterSend v-if="sendSheetOpen" />
		</AppBottomSheet>

		<!-- Send Test Email Bottom Sheet -->
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="sendTestSheetOpen" @toggling-popup="toggleSendTestSheet">
			<NewsletterSendTest v-if="sendTestSheetOpen" @close="sendTestSheetOpen = false" />
		</AppBottomSheet>

		<!-- More Options Bottom Sheet -->
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="moreOptionsSheetOpen" @toggling-popup="toggleMoreOptionsSheet">
			<div v-if="moreOptionsSheetOpen">
				<ModalHeader
					:title="getString('more_options')"
					:button-text="getString('save')"
					:button-loading="savingSubject"
					@click-button="handleSaveSubject" />
				<AppList :items="moreOptionsItems" />
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppSwitch from '@/shared/components/AppSwitch.vue';
import AppButton from '@/shared/components/AppButton.vue';
import AppBottomSheet from '@/shared/components/AppBottomSheet.vue';
import AppList from '@/shared/components/AppList.vue';
import NewsletterSend from './NewsletterSend.vue';
import NewsletterSendTest from './NewsletterSendTest.vue';
import ModalHeader from '@/console-new/components/shared/ModalHeader.vue';
import { useNewsletterStore } from '@/console-new/stores/newsletter.store';
import { useLanguageStore } from '@/shared/stores/language.store';
import { useUserStore } from '@/console-new/stores/user.store';
import { useNotificationStore } from "@/shared/stores";
import WindowWidth from '@/console-new/mixins/WindowWidth';
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import API from '@/shared/helpers/API.js';

export default {
	setup() {
		const newsletterStore = useNewsletterStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		return {
			newsletterStore,
			userStore,
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification
		};
	},
	name: "NewsletterEditNavbar",
	components: {
		AppButtonIcon,
		AppSwitch,
		AppButton,
		AppBottomSheet,
		AppList,
		NewsletterSend,
		NewsletterSendTest,
		ModalHeader
	},
	mixins: [WindowWidth],
	data() {
		return {
			sendSheetOpen: false,
			sendTestSheetOpen: false,
			moreOptionsSheetOpen: false,
			newSubject: '',
			savingSubject: false
		}
	},
	computed: {
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		currentNewsletter() {
			if (!this.newsletterStore.editingNewsletterId || !this.newsletterStore.businessNewsletters) {
				return null;
			}
			return this.newsletterStore.businessNewsletters.find(
				n => n.business_newsletter_id === this.newsletterStore.editingNewsletterId
			);
		},
		showSendButtons() {
			const status = this.currentNewsletter?.status;
			return status === 'draft' || status === 'scheduled';
		},
		moreOptionsItems() {
			const items = [
				{
					title: this.getString('new_subject'),
					textInput: true,
					value: this.newSubject,
					placeholder: this.currentNewsletter?.subject || '',
					callbackText: (value) => {
						this.newSubject = value;
					}
				},
				{
					title: this.getString('duplicate_newsletter'),
					buttonTitle: this.getString('duplicate'),
					callbackButton: this.handleDuplicateNewsletter
				}
			];

			// Only show delete option for drafts
			if (this.currentNewsletter?.status === 'draft') {
				items.push({
					title: this.getString('delete_newsletter'),
					buttonTitle: this.getString('delete'),
					callbackButton: this.handleDeleteNewsletter
				});
			}

			return items;
		}
	},
	methods: {
		togglePreviewMode(isDesktop) {
			this.newsletterStore.previewMode = isDesktop ? 'desktop' : 'mobile';
		},
		handleSend() {
			this.sendSheetOpen = true;
		},
		handleSendTest() {
			this.sendTestSheetOpen = true;
		},
		handleMoreOptions() {
			// Set initial value to current subject
			if (this.currentNewsletter && this.currentNewsletter.subject) {
				this.newSubject = this.currentNewsletter.subject;
			}
			this.moreOptionsSheetOpen = true;
		},
		toggleSendSheet(isOpen) {
			if (!isOpen) {
				this.sendSheetOpen = false;
			}
		},
		toggleSendTestSheet(isOpen) {
			if (!isOpen) {
				this.sendTestSheetOpen = false;
			}
		},
		toggleMoreOptionsSheet(isOpen) {
			if (!isOpen) {
				this.moreOptionsSheetOpen = false;
			}
		},
		handleSaveSubject() {
			if (!this.newSubject || !this.newSubject.trim()) {
				// No subject entered, nothing to save
				return;
			}

			if (this.newSubject.trim().length < 3) {
				// Subject too short
				return;
			}

			this.savingSubject = true;

			API.init().updateNewsletterSubject(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				this.newSubject.trim(),
				(response) => {
					this.savingSubject = false;
					if (response && response.result === 'OK') {
						// Update local newsletter object
						if (this.currentNewsletter) {
							this.currentNewsletter.subject = response.subject;
						}
						this.newSubject = '';
						this.moreOptionsSheetOpen = false;
					}
				}
			);
		},
		handleDuplicateNewsletter() {
			API.init().duplicateNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				(response) => {
					if (response && response.result === 'OK' && response.newsletter_id) {
						this.showNotification('generic-success');
						this.moreOptionsSheetOpen = false;

						// Force refresh of newsletter list, then navigate to the duplicated newsletter
						this.newsletterStore.businessNewsletters = null;
						this.newsletterStore.getBusinessNewsletters(() => {
							this.newsletterStore.editingNewsletterId = Number(response.newsletter_id);
						});
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		},
		handleDeleteNewsletter() {
			API.init().deleteNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				(response) => {
					if (response && response.result === 'OK') {
						this.showNotification('generic-success');
						this.moreOptionsSheetOpen = false;
						// Remove newsletter from local list
						if (this.newsletterStore.businessNewsletters) {
							this.newsletterStore.businessNewsletters = this.newsletterStore.businessNewsletters.filter(
								n => n.business_newsletter_id !== this.newsletterStore.editingNewsletterId
							);
						}
						// Navigate away from deleted newsletter
						this.newsletterStore.editingNewsletterId = null;
						this.$router.push('/marketing/newsletter');
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		}
	}
}
</script>

<style scoped lang="scss">
.newsletter-edit-navbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 10px;

	.navbar-left,
	.navbar-center,
	.navbar-right {
		flex: 1;
	}

	.navbar-center {
		display: flex;
		justify-content: center;

		@media (max-width: 850px) {
			display: none;
		}
	}

	.navbar-right {
		display: flex;
		justify-content: flex-end;
	}
}
</style>